// The door.
//
// Three ways in, and the one that needs nothing is the largest: an account buys
// photos, messages, the forum and a plan that survives this browser, and none of
// those are needed to read a schedule. Answering the door is recorded in this
// browser, so it is asked once rather than on every visit.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../data/account.ts';
import { useSession } from '../../data/session.tsx';
import '../../styles/account-refinements.css';

const SEEN_KEY = 'festival-scheduler-welcomed';

export function markWelcomed(): void {
  try {
    localStorage.setItem(SEEN_KEY, 'yes');
  } catch {
    // A browser refusing storage asks again, which costs one tap.
  }
}

export function wasWelcomed(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === 'yes';
  } catch {
    return false;
  }
}

export function Welcome() {
  const navigate = useNavigate();
  const { session, known, authenticate } = useSession();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [needsCode, setNeedsCode] = useState(false);
  const [code, setCode] = useState('');

  // Somebody already signed in on this device does not need a door.
  useEffect(() => {
    if (!known || !session?.user) return;
    markWelcomed();
    navigate('/home', { replace: true });
  }, [known, navigate, session?.user]);

  // Home rather than setup. The planner is one reason to be here and the door
  // should not decide it is the only one.
  const onward = () => {
    markWelcomed();
    navigate('/home', { replace: true });
  };

  const signIn = async () => {
    setBusy(true);
    setError('');
    const result = await authenticate(() => api.login({ identifier, password, code: code || undefined }));
    setBusy(false);
    if (result.data?.needsCode) {
      setNeedsCode(true);
      return;
    }
    if (!result.ok) {
      setError(result.error ?? 'That did not sign in.');
      return;
    }
    onward();
  };

  return (
    <div className="welcome">
      <div className="welcomecard">
        <h1>
          Lost Lands <small>2026</small>
        </h1>
        <p className="lede">
          Build your weekend, settle what clashes, and find the people you came with. The schedule
          is open to everybody.
        </p>

        <button className="guest" onClick={onward}>
          Continue as a guest
          <span>No account. The plan lives in this browser.</span>
        </button>

        <div className="welcomeor">
          <span>or sign in</span>
        </div>

        <form
          className="welcomeform"
          onSubmit={(e) => {
            e.preventDefault();
            void signIn();
          }}
        >
          <label className="field">
            <span>Username or email</span>
            <input
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {needsCode && (
            <label className="field">
              <span>Six digit code</span>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <span className="hint">From the authenticator app on this account.</span>
            </label>
          )}
          <button className="primary wide" disabled={busy || !identifier || !password}>
            {busy ? 'Signing in' : 'Sign in'}
          </button>
          {error && <p className="notice bad" role="status">{error}</p>}
        </form>

        <p className="welcomefoot">
          <Link to="/account?new=1" onClick={markWelcomed}>
            Make an account
          </Link>
          <span> · </span>
          <Link to="/account?forgot=1" onClick={markWelcomed}>
            Forgot password
          </Link>
        </p>

        <p className="muted small">
          An account adds photos, messages, the forum and a plan that follows you between devices.
        </p>
      </div>
    </div>
  );
}
